import React, { useCallback, useMemo } from 'react';
import { css } from '@emotion/css';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Select, Stack, Typography } from '@lyearn/ui';
import { WeekOfMonth, weekOfMonthById } from '@/components/Form/components/Frequency/conts';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import { CustomFrequencyInput, WeekOfMonthEnum } from '@/types/schema';

//todo generalise this
const VALUE_COUNT = 2;

const maxWidth = css`
  max-width: 100%;
`;

const inputStyle = css`
  min-width: 0 !important;
  width: 0 !important;
  margin-inline-start: 0;
  margin-inline-end: 0;
`;

const getValue = (item: { id: WeekOfMonthEnum; labelKey: string }, t: TFunction) => ({
  value: item.id,
  label: t(item.labelKey),
});

const getOptions = (items: { id: WeekOfMonthEnum; labelKey: string }[], t: TFunction) =>
  items.map((item) => getValue(item, t));

export interface Props {
  value: CustomFrequencyInput['weeksOfMonth'];
  onChange: (updatedValues: CustomFrequencyInput['weeksOfMonth']) => void;
  className?: string;
  error?: React.ReactNode;
}
const HabitWeeksOfMonth = ({ value, onChange, className, error }: Props) => {
  const { t } = useTranslation();
  const weeksOfMonth = value ? value.map((weekOfMonth) => weekOfMonthById[weekOfMonth].id) : [];
  const onValueChange = useCallback((value: WeekOfMonthEnum[]) => onChange(value), [onChange]);

  const options = useMemo(() => getOptions(WeekOfMonth, t), [t]);

  const renderTags = useCallback((values: typeof options) => {
    if (values?.length === 1) {
      return (
        <Typography color="text-text-paragraph" numberOfLines={1} variant="body-short-01">
          {values[0].label}
        </Typography>
      );
    }

    const labels = values?.reduce((acc, it, index) => {
      if (index > VALUE_COUNT - 1) return acc;
      if (it) acc.push(it.label);

      return acc;
    }, [] as string[]);

    return (
      <Box className={cn(maxWidth, 'flex items-center overflow-hidden')}>
        <Typography
          className="inline-block"
          color="text-text-paragraph"
          numberOfLines={1}
          variant="body-short-01">
          {labels?.join(', ')}
        </Typography>
        {values?.length <= VALUE_COUNT ? null : (
          <Typography
            className="inline-block ps-4 pe-8"
            color="text-text-placeholder"
            variant="body-short-01">
            {`+${values?.length - VALUE_COUNT}`}
          </Typography>
        )}
      </Box>
    );
  }, []);

  return (
    <Stack className={className ?? 'flex flex-1'} direction="col" gapY={8}>
      <Select
        disableClearable
        multiple
        classes={weeksOfMonth.length ? { input: inputStyle } : {}}
        error={!!error}
        getOptionLabel={getSelectOptionLabel}
        options={options}
        placeholder={weeksOfMonth.length ? '' : t('Habits:Editor.SelectWeek')}
        renderTags={renderTags}
        renderValue={(value) => renderTags(value ? [value] : value)}
        size="large"
        value={weeksOfMonth}
        onChange={onValueChange}
      />
      {error ? (
        <Typography className="ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default HabitWeeksOfMonth;
